import React, { useEffect } from 'react'
import { BodyText, HeadingText, TitleText } from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import ButtonFullWidth from '../../components/ButtonFullWidth'
import { ERROR_CODES, HOME_PATH, userType } from '../../constants'
import {
  clearHandledError,
  setHandledError,
} from '../../store/slices/handledErrorSlice'
import {
  setCreateGroup,
  setCreateGroupFlow,
  toggleCreateSuccess,
  toggleFocusOnCoverImageEditButton,
} from '../../store/slices/sheetsSlice'
import { useNavigate } from '@reach/router'
import SheetWrapper from '../../components/SheetWrapper'
import { GroupFormFields } from './components/GroupFormFields'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useCreateGroupMutation } from '../../services/createGroupApi'
import { useLocalizedContent } from '../../hooks/useLocalizedContent'
import { clearForm, selectGroupForm } from '../../store/slices/groupFormSlice'
import { createGroupPayload } from '../../utils/groupForm/createPayload'
import { useLazyGetCurrentUserGroupsQuery } from '../../services/groupApi'
import { tracking } from '../../lib/analytics/fpti'
import Events from '../../lib/analytics/eventTags.js'
import serverData from '../../utils/server-data'
import { isCoverOnCreateFlowEnabled } from '../../utils/features'
import * as styles from './createPool.css'

const CreatePool = ({
  isOpen,
  isLoading,
  onCloseComplete,
  redirectHomeAfterClosing = true,
}: {
  isOpen: boolean
  isLoading?: boolean
  onCloseComplete?: () => void
  redirectHomeAfterClosing?: boolean
}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [createGroupMutation, { isLoading: isCreatingGroup }] =
    useCreateGroupMutation({ fixedCacheKey: 'create-pool' })
  const [refetchCurrentUserGroupsList] = useLazyGetCurrentUserGroupsQuery()
  const focusOnCoverImageEditButton = useAppSelector(
    ({ sheets }) => sheets.focusOnCoverImageEditButton,
  )
  const { getContent } = useLocalizedContent()
  const groupForm = useAppSelector(selectGroupForm)

  useEffect(() => {
    return () => {
      dispatch(toggleFocusOnCoverImageEditButton(false))
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      tracking(
        Events.p2p_pools_create_screen_shown({
          source: serverData.trackingParameters.externalSource,
          page_variant: 'creator',
          ...serverData.trackingParameters.pxpTracking,
        }),
      )
    }
  }, [isOpen])

  const goToHomePage = () => {
    dispatch(setCreateGroupFlow({ toggleCreateGroupFlow: false }))
    dispatch(setCreateGroup(false))
    dispatch(clearHandledError())
    dispatch(clearForm())

    if (onCloseComplete) {
      onCloseComplete()
    }

    if (redirectHomeAfterClosing) {
      navigate(HOME_PATH)
    }
  }

  // Function to indicate if the Sheet can be closed.
  // Prevent it when the create group call is in progress
  const canCloseSheet = () => {
    return !isCreatingGroup
  }

  const handleSubmit = async () => {
    tracking(
      Events.p2p_pools_create_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: 'creator',
      }),
    )
    const createPoolInput = {
      input: {
        ...createGroupPayload(groupForm),
      },
    }
    try {
      await createGroupMutation(createPoolInput).unwrap()
      refetchCurrentUserGroupsList(
        { userType: userType.CREATOR, startCursor: 1 },
        false,
      )
      dispatch(toggleCreateSuccess())
      return goToHomePage()
    } catch (error) {
      tracking(
        Events.p2p_failed_customer_interaction_occurred({
          source: serverData.trackingParameters.externalSource,
          error_code: error.data?.error?.code,
        }),
      )
      const isConnectionError =
        error.status?.toString() === ERROR_CODES.FETCH_ERROR
      if (isConnectionError && isCoverOnCreateFlowEnabled()) {
        return dispatch(
          setHandledError({
            data: {
              error: {
                code: ERROR_CODES.UNABLE_CREATE,
                message: 'Connection Error',
                name: 'Error',
                statusCode: 400,
                type: 'api',
              },
            },
          })(dispatch),
        )
      }
      return dispatch(setHandledError(error)(dispatch))
    }
  }

  const onClickEditImage = () => {
    dispatch(setCreateGroupFlow({ toggleCreateGroupFlow: false }))
    dispatch(setCreateGroup(false))
  }

  const footerContents = isCreatingGroup ? null : (
    <ButtonFullWidth
      data-testid="agree-and-create"
      type="submit"
      form="groupForm"
    >
      <Message id="create.view.agreeAndCreate" />
    </ButtonFullWidth>
  )

  return (
    <SheetWrapper
      title={getContent('create.view.sheetTitle')}
      isOpen={isOpen}
      onCloseComplete={goToHomePage}
      data-testid="create-pool-sheet"
      footerContents={footerContents}
      center={false}
      hideTitle={true}
      onBeforeClose={canCloseSheet}
      isLoading={isCreatingGroup || isLoading}
      skipFormFocus={!focusOnCoverImageEditButton}
    >
      <HeadingText
        as="h1"
        size={isCoverOnCreateFlowEnabled() ? 'lg' : 'sm'}
        className={styles.heading}
      >
        <Message id="create.view.headingContent" />
      </HeadingText>
      {isCoverOnCreateFlowEnabled() ? (
        <TitleText as="p" className={styles.subheading}>
          <Message id="create.view.subheading" />
        </TitleText>
      ) : (
        <BodyText as="p">
          <Message id="create.view.subheading" />
        </BodyText>
      )}

      <GroupFormFields
        displayTerms
        handleSubmit={handleSubmit}
        onClickEditImage={onClickEditImage}
      />
    </SheetWrapper>
  )
}

export default CreatePool
